import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { KeyRound, Delete } from 'lucide-react';

export default function PinSetupPage() {
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [step, setStep] = useState(1); // 1 = enter, 2 = confirm
  const [loading, setLoading] = useState(false);
  const { setupPin, user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const pinLength = 6;

  const handleNumberClick = (num) => {
    const currentPin = step === 1 ? pin : confirmPin;
    if (currentPin.length < pinLength) {
      if (step === 1) {
        setPin(prev => prev + num);
      } else {
        setConfirmPin(prev => prev + num);
      }
    }
  };

  const handleDelete = () => {
    if (step === 1) {
      setPin(prev => prev.slice(0, -1));
    } else {
      setConfirmPin(prev => prev.slice(0, -1));
    }
  };

  const handleContinue = async () => {
    if (step === 1 && pin.length === pinLength) {
      setStep(2);
    } else if (step === 2 && confirmPin.length === pinLength) {
      if (pin !== confirmPin) {
        toast.error(t('error'), { description: 'Les PINs ne correspondent pas' });
        setConfirmPin('');
        return;
      }

      setLoading(true);
      try {
        const result = await setupPin(pin);
        if (result.success) {
          toast.success(t('success'), { description: 'PIN configuré avec succès' });
        } else {
          toast.error(t('error'), { description: result.error || 'Erreur configuration PIN' });
          setLoading(false);
          return;
        }
        
        // Redirect based on role
        switch (user?.role) {
          case 'super_admin':
          case 'admin_ops':
          case 'bureau':
            navigate('/admin');
            break;
          case 'analyst':
            navigate('/analyst');
            break;
          case 'client':
            navigate('/client');
            break;
          default:
            navigate('/login');
        }
      } catch (error) {
        toast.error(t('error'), { description: 'Erreur lors de la configuration du PIN' });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSkip = () => {
    switch (user?.role) {
      case 'super_admin':
      case 'admin_ops':
          case 'bureau':
        navigate('/admin');
        break;
      case 'analyst':
        navigate('/analyst');
        break;
      case 'client':
        navigate('/client');
        break;
      default:
        navigate('/login');
    }
  };

  const currentPin = step === 1 ? pin : confirmPin;

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-background">
      <div className="w-full max-w-sm">
        {/* Icon */}
        <div className="flex justify-center mb-8">
          <div className="w-20 h-20 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
            <KeyRound className="w-10 h-10 text-[#D4AF37]" />
          </div>
        </div>

        {/* Title */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
            {t('pin_setup_title')}
          </h1>
          <p className="text-muted-foreground">
            {step === 1 ? t('enter_pin') : 'Confirmez votre PIN'}
          </p>
        </div>

        {/* PIN Display */}
        <div className="flex justify-center gap-3 mb-8">
          {Array.from({ length: pinLength }).map((_, i) => (
            <div
              key={i}
              className={`w-4 h-4 rounded-full border-2 transition-colors ${
                i < currentPin.length 
                  ? 'bg-[#D4AF37] border-[#D4AF37]' 
                  : 'border-gray-600'
              }`}
            />
          ))}
        </div>

        {/* Keypad */}
        <div className="grid grid-cols-3 gap-4 max-w-xs mx-auto mb-8">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
            <button
              key={num}
              onClick={() => handleNumberClick(num.toString())}
              className="h-16 rounded-sm bg-card border border-border text-xl font-semibold hover:bg-white/5 transition-colors"
              data-testid={`pin-key-${num}`}
            >
              {num}
            </button>
          ))}
          <div /> {/* Empty cell */}
          <button
            onClick={() => handleNumberClick('0')}
            className="h-16 rounded-sm bg-card border border-border text-xl font-semibold hover:bg-white/5 transition-colors"
            data-testid="pin-key-0"
          >
            0
          </button>
          <button
            onClick={handleDelete}
            className="h-16 rounded-sm bg-card border border-border flex items-center justify-center hover:bg-white/5 transition-colors"
            data-testid="pin-delete"
          >
            <Delete className="w-6 h-6" />
          </button>
        </div>

        {/* Actions */}
        <div className="space-y-3">
          <Button
            onClick={handleContinue}
            disabled={currentPin.length !== pinLength || loading}
            className="w-full h-12 btn-gold"
            data-testid="pin-continue-btn"
          >
            {loading ? t('loading') : (step === 1 ? t('next') : t('confirm'))}
          </Button>
          
          <Button
            onClick={handleSkip}
            variant="ghost"
            className="w-full h-12 text-muted-foreground"
            data-testid="pin-skip-btn"
          >
            Ignorer pour l'instant
          </Button>
        </div>
      </div>
    </div>
  );
}
